import React, { useContext, useRef, useState, useEffect } from "react";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { API } from "aws-amplify";
import {
  Button,
  IconButton,
  InputLabel,
  Stack,
  Typography,
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useForm } from "react-hook-form";
import { DialogWindow } from "UI/DialogWindow";
import { FormTextField } from "UI/FormTextField";
import {
  getPasswordConfirmValidation,
  emailValidation,
  passwordValidation,
  nameValidation,
} from "models/validations";
import { GlobalContext } from "store/globalContext";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { useCloseAndReset } from "hooks/useCloseAndReset";

/** @type {import("types/commonTypes").NewUserData}*/
const defaultFormData = {
  role: "Admin",
  organizationId: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  password_repeat: "",
};

export const AddNewRecipientAdminDialog = ({
  recipientId,
  // adminEmail,
  recipientName,
  handleClose,
  refreshList,
}) => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const { showWarning } = useContext(GlobalContext);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: defaultFormData,
  });

  const forceCloseAndReset = () => {
    reset(defaultFormData);
    handleClose();
  };

  const onSubmit = createApiCall(async () => {
    const user = getValues();
    const result = await API.put("restApi", "/users", {
      body: {
        user,
        recipientName,
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Recipient Admin added successfully
          </Typography>
        </Stack>
      ),
      content: <></>,
    });

    refreshList();
    forceCloseAndReset();
  });

  const closeAndReset = useCloseAndReset(forceCloseAndReset, isDirty);

  const password = useRef("");
  password.current = watch("password", "");

  useEffect(() => {
    if (recipientId)
      reset({
        ...defaultFormData,
        organizationId: recipientId,
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientId]);

  return (
    <DialogWindow
      open={!!recipientId}
      withCloseButton
      handleClose={closeAndReset}
      title={"Add Recipient Admin"}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack mt={3} gap={3}>
            {/* <Stack direction={"row"} justifyContent={"space-between"} gap={2}>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="role">
                  Role
                </InputLabel>
                <FormTextField
                  id="role"
                  name="role"
                  control={control}
                  rules={{ required }}
                  InputProps={{
                    autoComplete: "off",
                  }}
                  disabled
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="recipientName">Recipient</InputLabel>
                <TextField
                  id="recipientName"
                  disabled
                  defaultValue={recipientName}
                  variant="filled"
                  size="small"
                />
              </Stack>
            </Stack> */}
            <Stack direction={"row"} gap={1}>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="firstName">First name</InputLabel>
                <FormTextField
                  id="firstName"
                  name="firstName"
                  control={control}
                  rules={nameValidation}
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="lastName">Last name</InputLabel>
                <FormTextField
                  id="lastName"
                  name="lastName"
                  control={control}
                  rules={nameValidation}
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
            </Stack>
            <Stack alignItems={"flex-start"}>
              <InputLabel htmlFor="email">email</InputLabel>
              <FormTextField
                id="email"
                name="email"
                control={control}
                rules={emailValidation}
                InputProps={{
                  autoComplete: "off",
                }}
              />
            </Stack>
            <Stack gap={1}>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="password">Create password</InputLabel>
                <FormTextField
                  id="password"
                  name="password"
                  control={control}
                  rules={passwordValidation}
                  type={isPasswordVisible ? "text" : "password"}
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="password_repeat">
                  Repeat password
                </InputLabel>
                <FormTextField
                  id="password_repeat"
                  name="password_repeat"
                  control={control}
                  rules={getPasswordConfirmValidation(password.current)}
                  type={isPasswordVisible ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        color="info"
                        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      >
                        {isPasswordVisible ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    ),
                    autoComplete: "off",
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
        </form>
      }
      actions={
        <Stack
          mt={2}
          mb={2}
          justifyContent={"center"}
          alignItems={"center"}
          width="100%"
        >
          <Button variant="contained" onClick={handleSubmit(onSubmit)}>
            Add Recipient Admin
          </Button>
        </Stack>
      }
    />
  );
};
